import { useTranslation } from "react-i18next";
import { Divider, Form, Icon } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { createFormHelper } from "lib/Form";
import { KEYLABEL_LAYOUTS, KEYLABEL_TYPES } from "models";
import { enumToDropdownOptions } from "utils/common";

import type { LocationDetailFormPayload } from "modules/LocationOverviewDashboard/hooks";

const FormHelper = createFormHelper<LocationDetailFormPayload>();

type KeylabelKeylockerProps = {
  tabId: string;
};

type KeyLabelLayoutConfig = {
  label: string;
  fallback: string;
  dims?: string;
};

const KEYLABEL_LAYOUT_CONFIG = new Map<KEYLABEL_LAYOUTS, KeyLabelLayoutConfig>([
  [KEYLABEL_LAYOUTS.A4, { label: "v8_a4", fallback: "A4" }],
  [KEYLABEL_LAYOUTS.Label, { label: "v8_label", fallback: "Label", dims: "29 x 62 mm" }],
  [KEYLABEL_LAYOUTS.LongLabel, { label: "v8_label", fallback: "Label", dims: "29 x 90 mm" }],
  [KEYLABEL_LAYOUTS.KioskLabel, { label: "v8_kiosk_label", fallback: "Kiosk Label", dims: "19.1 x 279.4 mm" }],
  [KEYLABEL_LAYOUTS.KeyTag, { label: "v8_keytag", fallback: "Keytag", dims: "35 x 108 mm" }],
  [KEYLABEL_LAYOUTS.LabelWriter400, { label: "v8_labelwriter_400", fallback: "LabelWriter 400", dims: "57 x 32 mm" }],
  [KEYLABEL_LAYOUTS.AltecQR, { label: "v8_altec_qr", fallback: "Altec QR" }]
]);

export const KeylabelKeylocker = ({ tabId }: KeylabelKeylockerProps) => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const keylockerCode = watch("keylocker_code");

  const labelTypeOptions = enumToDropdownOptions({ enumObj: KEYLABEL_TYPES, t });
  const labelLayoutOptions = [...KEYLABEL_LAYOUT_CONFIG].map(([value, { label, fallback, dims }]) => ({
    key: value,
    value,
    text: dims ? `${t(label, fallback)} - ${dims}` : t(label, fallback)
  }));

  return (
    <Element name={tabId} className="location-overview-section">
      <div className="section-title">
        <div>
          <Icon className="key" />
        </div>
        <span className="section-title-text">{t("v8_keylabel_keylocker", "Keylabel & Keylocker")}</span>
      </div>

      <div className="location-overview-subheading">{t("v8_key_label", "Key Label")}</div>
      <Form.Group>
        <Form.Field width={8}>
          <label>{t("v8_label_format", "Label Format")}</label>
          <FormHelper.Field.Dropdown name="key_label_type_id" options={labelTypeOptions} />
        </Form.Field>
        <Form.Field width={8}>
          <label>{t("v8_label_printer", "Label Printer")}</label>
          <FormHelper.Field.Dropdown name="key_label_layout_id" options={labelLayoutOptions} />
        </Form.Field>
      </Form.Group>
      <Divider />

      <div className="location-overview-subheading">{t("v8_keylocker", "Keylocker")}</div>
      <Form.Group>
        <Form.Field width={8} error={formState.isSubmitted && (!keylockerCode || !(keylockerCode >= 1000 && keylockerCode <= 9999))}>
          <label>{t("v8_tablet_security_code", "Tablet security code")}</label>
          <FormHelper.Field.Input nullable name="keylocker_code" inputProps={{ type: "number" }} />
        </Form.Field>
      </Form.Group>
    </Element>
  );
};
